import { useState, useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { ApiService, AuthRedirectError } from '../services/apiService';
import { getFriendlyApiErrorMessage, logApiError } from '../services/apiErrorMessages';

export interface ApiRequestState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseApiRequestResult<T> extends ApiRequestState<T> {
  execute: (fn: (api: ApiService) => Promise<T>) => Promise<void>;
}

/**
 * Hook that provides an ApiService instance backed by the current MSAL session,
 * along with managed loading/error/data state.
 *
 * Usage:
 * ```
 * const { data, isLoading, error, execute } = useApiRequest<MyType>();
 *
 * useEffect(() => {
 *   execute((api) => api.get<MyType>('/api/my-endpoint'));
 * }, [execute]);
 * ```
 */
export function useApiRequest<T>(): UseApiRequestResult<T> {
  const { instance, accounts } = useMsal();
  const [state, setState] = useState<ApiRequestState<T>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const execute = useCallback(
    async (fn: (api: ApiService) => Promise<T>) => {
      const account = instance.getActiveAccount() ?? accounts[0];
      if (!account) {
        setState({ data: null, isLoading: false, error: 'No authenticated account found.' });
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const api = new ApiService(instance, account);
        const result = await fn(api);

        // Only update state if this is still the latest request and component is mounted
        if (mountedRef.current && currentRequestId === requestIdRef.current) {
          setState({ data: result, isLoading: false, error: null });
        }
      } catch (err) {
        if (err instanceof AuthRedirectError) {
          // Page is navigating away — don't update state
          return;
        }

        logApiError('useApiRequest', err);
        const message = getFriendlyApiErrorMessage(err);

        if (mountedRef.current && currentRequestId === requestIdRef.current) {
          setState({ data: null, isLoading: false, error: message });
        }
      }
    },
    [instance, accounts]
  );

  return { ...state, execute };
}
